import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable';
import useFetch from '../../hooks/useFetch';
import styles from '../../styles/encuestas.module.css';

const BASE_ENCUESTAS = '/gestionEncuestas';
const BASE_EQUIPOS = '/gestionEquipos';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const formatFecha = (value) => {
    if (value == null || value === '') return '—';
    const str = typeof value === 'string' ? value : (value && value.toISOString?.().slice(0, 10));
    if (!str) return '—';
    const [y, m, d] = str.slice(0, 10).split('-').map(Number);
    const mes = MESES[m - 1] || '';
    return `${d} ${mes} ${y}`;
};

const toDateInputValue = (value) => {
    if (value == null || value === '') return '';
    const str = typeof value === 'string' ? value : (value && value.toISOString?.().slice(0, 10));
    return str ? str.slice(0, 10) : '';
};

/** Día anterior a la fecha de cierre (mismo criterio que el recordatorio 24h). */
const diaEnvioRecordatorio24h = (fechaFin) => {
    const ymd = toDateInputValue(fechaFin);
    if (!ymd) return null;
    const d = new Date(`${ymd}T12:00:00`);
    d.setDate(d.getDate() - 1);
    return formatFecha(d.toISOString());
};

const clearChoiceSelection = () => {
    window.getSelection()?.removeAllRanges();
};

const labelAsignacion = (row) => {
    if (row.tipo === 'individual' || row.tipo === 'freemium') return row.nombre_completo || '—';
    return row.nombre_equipo || '—';
};

const labelTipo = (tipo) => (
    { individual: 'Individual', equipo: 'Equipo', freemium: 'Freemium' }[tipo] || tipo || '—'
);

const labelEstado = (estado) => (
    { programada: 'Programada', activa: 'Activa', finalizada: 'Finalizada' }[estado] || estado || '—'
);

const badgeEstadoClass = (estado, styles) => {
    if (estado === 'programada') return styles.badgeProgramada;
    if (estado === 'activa') return styles.badgeActiva;
    if (estado === 'finalizada') return styles.badgeFinalizada;
    return styles.badgeNeutral;
};

const Encuestas = () => {
    const navigate = useNavigate();
    const { execute } = useFetch();
    const [encuestas, setEncuestas] = useState([]);
    const [loadingEncuestas, setLoadingEncuestas] = useState(true);
    const [listError, setListError] = useState(null);
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingEncuesta, setEditingEncuesta] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [finalizarConfirm, setFinalizarConfirm] = useState(false);
    const [equiposList, setEquiposList] = useState([]);
    const [loadingEquipos, setLoadingEquipos] = useState(false);
    const [tipoEncuesta, setTipoEncuesta] = useState('equipo');
    const [idEquipo, setIdEquipo] = useState('');
    const [personaNombre, setPersonaNombre] = useState('');
    const [personaApellido, setPersonaApellido] = useState('');
    const [personaEmail, setPersonaEmail] = useState('');
    const [modoEntrega, setModoEntrega] = useState('correo');
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [fechaFinOriginal, setFechaFinOriginal] = useState('');
    const [enviarCorreoPlazo, setEnviarCorreoPlazo] = useState(false);
    const [formError, setFormError] = useState('');
    const [detalleActionLoading, setDetalleActionLoading] = useState(false);
    const [detalleActionError, setDetalleActionError] = useState('');
    const [enviarConfirm, setEnviarConfirm] = useState(null);
    const [preflightLoading, setPreflightLoading] = useState(false);
    const [preflightResult, setPreflightResult] = useState(null);
    const [enviarLoading, setEnviarLoading] = useState(false);
    const [extraActivo, setExtraActivo] = useState(false);
    const [extraFecha, setExtraFecha] = useState('');
    const [recordatorioLoading, setRecordatorioLoading] = useState(false);
    const [enlaceDetalle, setEnlaceDetalle] = useState('');
    const [loadingEnlace, setLoadingEnlace] = useState(false);
    const [enlaceCopiado, setEnlaceCopiado] = useState(false);
    const [reabrirFechaFin, setReabrirFechaFin] = useState('');
    const [reabrirFechaFinOriginal, setReabrirFechaFinOriginal] = useState('');
    const [reabrirEnviarCorreoPlazo, setReabrirEnviarCorreoPlazo] = useState(false);
    const [toastMsg, setToastMsg] = useState('');
    const [filtroTipo, setFiltroTipo] = useState('todos');
    const [filtroEmpresa, setFiltroEmpresa] = useState('todas');

    const empresasDisponibles = useMemo(() => {
        const names = new Set();
        encuestas.forEach((e) => {
            if (e.tipo === 'equipo' && e.nombre_empresa) names.add(e.nombre_empresa);
        });
        return [...names].sort((a, b) => a.localeCompare(b, 'es'));
    }, [encuestas]);

    const encuestasFiltradas = useMemo(() => encuestas.filter((e) => {
        if (filtroTipo !== 'todos' && e.tipo !== filtroTipo) return false;
        if (filtroEmpresa !== 'todas' && filtroTipo !== 'individual' && filtroTipo !== 'freemium') {
            if (e.tipo !== 'equipo') return false;
            if ((e.nombre_empresa || '') !== filtroEmpresa) return false;
        }
        return true;
    }), [encuestas, filtroTipo, filtroEmpresa]);

    const conteoPorTipo = useMemo(() => {
        const base = filtroEmpresa === 'todas'
            ? encuestas
            : encuestas.filter((e) => e.tipo === 'equipo' && e.nombre_empresa === filtroEmpresa);
        return {
            todos: base.length,
            equipo: base.filter((e) => e.tipo === 'equipo').length,
            individual: base.filter((e) => e.tipo === 'individual').length,
            freemium: base.filter((e) => e.tipo === 'freemium').length,
        };
    }, [encuestas, filtroEmpresa]);

    const conteoPorEmpresa = useMemo(() => {
        const base = filtroTipo === 'todos'
            ? encuestas
            : encuestas.filter((e) => e.tipo === filtroTipo);
        const map = {};
        base.forEach((e) => {
            if (e.tipo === 'equipo' && e.nombre_empresa) {
                map[e.nombre_empresa] = (map[e.nombre_empresa] || 0) + 1;
            }
        });
        return map;
    }, [encuestas, filtroTipo]);

    const filtrosTipo = [
        { id: 'todos', label: 'Todas' },
        { id: 'equipo', label: 'Equipo' },
        { id: 'individual', label: 'Individual' },
        { id: 'freemium', label: 'Freemium' },
    ];

    const initGestionState = (encuesta) => {
        setDetalleActionError('');
        setExtraActivo(!!encuesta.recordatorio_extra_activo);
        setExtraFecha(toDateInputValue(encuesta.recordatorio_extra_fecha));
        setEnlaceDetalle('');
        setEnlaceCopiado(false);
        setReabrirFechaFin(toDateInputValue(encuesta.fecha_fin));
        setReabrirFechaFinOriginal(toDateInputValue(encuesta.fecha_fin));
        setReabrirEnviarCorreoPlazo(false);
        setFinalizarConfirm(false);
        if (encuesta.tipo === 'individual' || encuesta.tipo === 'freemium') {
            cargarEnlaceEncuesta(encuesta);
        }
    };

    const closeFormModal = () => {
        setShowFormModal(false);
        setEditingEncuesta(null);
        setDetalleActionError('');
        setFinalizarConfirm(false);
    };

    const goToResultados = (encuesta) => {
        navigate(`/creatividades/respuestas?encuestaId=${encuesta.id_encuesta}`);
    };

    const syncEditingEncuesta = (patch) => {
        setEditingEncuesta((prev) => (prev ? { ...prev, ...patch } : prev));
    };

    const showToast = (msg) => {
        setToastMsg(msg);
        window.setTimeout(() => setToastMsg(''), 2500);
    };

    const copiarTexto = async (texto) => {
        if (!texto) return false;
        try {
            await navigator.clipboard.writeText(texto);
            return true;
        } catch {
            return false;
        }
    };

    const cargarEnlaceEncuesta = async (encuesta, idUsuarioParam) => {
        if (!encuesta?.id_encuesta) return;
        setLoadingEnlace(true);
        setEnlaceCopiado(false);
        try {
            const body = { id_encuesta: encuesta.id_encuesta };
            const uid = idUsuarioParam ?? encuesta.id_usuario;
            if (uid) body.id_usuario = uid;
            const res = await execute(`${BASE_ENCUESTAS}/obtenerEnlaceEncuesta`, 'POST', body, { silent: true });
            setEnlaceDetalle(res?.data?.enlace || '');
        } catch (err) {
            setDetalleActionError(err?.message || 'No se pudo obtener el enlace');
        } finally {
            setLoadingEnlace(false);
        }
    };

    const handleCopiarEnlace = async (enlace) => {
        const ok = await copiarTexto(enlace);
        if (ok) {
            setEnlaceCopiado(true);
            showToast('Enlace copiado al portapapeles');
            window.setTimeout(() => setEnlaceCopiado(false), 2000);
        }
    };

    const aplicarFechaInicioHoySiActiva = (modo) => {
        if (modo === 'correo' || modo === 'enlace') {
            setFechaInicio(toDateInputValue(new Date()));
        }
    };

    const handleModoEntregaChange = (modo) => {
        setModoEntrega(modo);
        aplicarFechaInicioHoySiActiva(modo);
    };

    const handleTipoEncuestaChange = (tipo) => {
        setTipoEncuesta(tipo);
        if (tipo === 'individual') {
            aplicarFechaInicioHoySiActiva(modoEntrega);
        }
    };

    const fetchEncuestas = useCallback(async () => {
        setLoadingEncuestas(true);
        setListError(null);
        try {
            const res = await execute(`${BASE_ENCUESTAS}/encuestas`, 'GET', null, { silent: true });
            setEncuestas(Array.isArray(res?.data) ? res.data : []);
        } catch (err) {
            setEncuestas([]);
            setListError(err);
        } finally {
            setLoadingEncuestas(false);
        }
    }, [execute]);

    // Cargar listado al montar el componente
    useEffect(() => {
        fetchEncuestas();
    }, [fetchEncuestas]);

    const openCreate = async () => {
        setEditingEncuesta(null);
        setTipoEncuesta('equipo');
        setIdEquipo('');
        setPersonaNombre('');
        setPersonaApellido('');
        setPersonaEmail('');
        setModoEntrega('correo');
        setFechaInicio('');
        setFechaFin('');
        setFormError('');
        if (equiposList.length === 0) {
            setLoadingEquipos(true);
            try {
                const equiposRes = await execute(`${BASE_EQUIPOS}/equipos`, 'GET', null, { silent: true });
                setEquiposList(Array.isArray(equiposRes?.data) ? equiposRes.data : []);
            } finally {
                setLoadingEquipos(false);
            }
        }
        setShowFormModal(true);
    };

    const openEdit = (encuesta) => {
        setEditingEncuesta(encuesta);
        setFechaInicio(toDateInputValue(encuesta.fecha_inicio));
        const fin = toDateInputValue(encuesta.fecha_fin);
        setFechaFin(fin);
        setFechaFinOriginal(fin);
        setEnviarCorreoPlazo(false);
        setFormError('');
        initGestionState(encuesta);
        setShowFormModal(true);
    };

    const muestraOpcionCorreoPlazo = useMemo(() => {
        if (!editingEncuesta || editingEncuesta.estado !== 'activa') return false;
        if (!fechaFinOriginal) return true;
        return fechaFin > fechaFinOriginal;
    }, [editingEncuesta, fechaFin, fechaFinOriginal]);

    const muestraOpcionCorreoReabrir = useMemo(() => {
        if (editingEncuesta?.estado !== 'finalizada') return false;
        const fin = reabrirFechaFin.trim();
        if (!fin) return false;
        const original = reabrirFechaFinOriginal.trim();
        if (!original) return true;
        return fin > original;
    }, [editingEncuesta, reabrirFechaFin, reabrirFechaFinOriginal]);

    const handleSaveEncuesta = async (e) => {
        e.preventDefault();
        setFormError('');
        const inicio = fechaInicio.trim();
        const fin = fechaFin.trim();

        if (!editingEncuesta && !inicio) {
            setFormError('La fecha de inicio es obligatoria para crear una encuesta.');
            return;
        }
        if (!fin) {
            setFormError('La fecha de cierre es obligatoria.');
            return;
        }
        if (inicio && fin && new Date(fin) < new Date(inicio)) {
            setFormError('La fecha de fin no puede ser anterior a la fecha de inicio.');
            return;
        }

        try {
            if (editingEncuesta) {
                const res = await execute(`${BASE_ENCUESTAS}/editarEncuesta`, 'PUT', {
                    id_encuesta: editingEncuesta.id_encuesta,
                    tipo: editingEncuesta.tipo,
                    fecha_inicio: inicio,
                    fecha_fin: fin,
                    ...(muestraOpcionCorreoPlazo ? { enviar_correo_plazo: enviarCorreoPlazo } : {}),
                }, { silent: true });
                const enviados = res?.data?.correos_plazo_enviados;
                if (typeof enviados === 'number' && enviados > 0) {
                    showToast(`Plazo actualizado. Correo de ampliación enviado a ${enviados} participante${enviados === 1 ? '' : 's'}.`);
                } else {
                    showToast('Plazos actualizados');
                }
                syncEditingEncuesta(res?.data || { fecha_inicio: inicio, fecha_fin: fin });
                setFechaFinOriginal(fin);
                setEnviarCorreoPlazo(false);
                await fetchEncuestas();
                return;
            } else if (tipoEncuesta === 'individual') {
                const nombre = personaNombre.trim();
                const apellido = personaApellido.trim();
                const email = personaEmail.trim().toLowerCase();
                if (!nombre || !apellido || !email) {
                    setFormError('Nombre, apellido y correo son obligatorios.');
                    return;
                }
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                    setFormError('Introduce un correo electrónico válido.');
                    return;
                }
                const activarAhora = modoEntrega !== 'programar';
                const enviarCorreo = modoEntrega === 'correo';
                const res = await execute(`${BASE_ENCUESTAS}/crearEncuesta`, 'POST', {
                    tipo: 'individual',
                    nombre,
                    apellido,
                    email,
                    fecha_inicio: inicio,
                    fecha_fin: fin,
                    activar_ahora: activarAhora,
                    enviar_correo: enviarCorreo,
                }, { silent: true });
                const creada = res?.data;
                await fetchEncuestas();
                setShowFormModal(false);
                setEditingEncuesta(null);
                if (creada) {
                    openEdit(creada);
                }
                return;
            } else {
                const id = idEquipo ? Number(idEquipo) : null;
                if (!id) {
                    setFormError('Selecciona un equipo.');
                    return;
                }
                await execute(`${BASE_ENCUESTAS}/crearEncuesta`, 'POST', {
                    tipo: 'equipo',
                    id_equipo: id,
                    fecha_inicio: inicio,
                    fecha_fin: fin,
                }, { silent: true });
            }
            setShowFormModal(false);
            setEditingEncuesta(null);
            await fetchEncuestas();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message;
            if (msg) setFormError(msg);
        }
    };

    const handleDeleteEncuesta = async (encuesta) => {
        try {
            await execute(`${BASE_ENCUESTAS}/eliminarEncuesta`, 'POST', { id_encuesta: encuesta.id_encuesta }, { silent: true });
            setDeleteConfirm(null);
            if (editingEncuesta?.id_encuesta === encuesta.id_encuesta) closeFormModal();
            await fetchEncuestas();
        } catch {
            // error ya lo gestiona useFetch
        }
    };

    const handleGuardarRecordatorioExtra = async () => {
        if (!editingEncuesta?.id_encuesta) return;
        setDetalleActionError('');
        if (extraActivo && !extraFecha.trim()) {
            setDetalleActionError('Indica la fecha de envío del recordatorio extra.');
            return;
        }
        setRecordatorioLoading(true);
        try {
            const res = await execute(`${BASE_ENCUESTAS}/configurarRecordatorioExtra`, 'PUT', {
                id_encuesta: editingEncuesta.id_encuesta,
                activo: extraActivo,
                fecha: extraActivo ? extraFecha.trim() : null,
            }, { silent: true });
            if (res?.data?.encuesta) {
                const merged = { ...editingEncuesta, ...res.data.encuesta };
                syncEditingEncuesta(merged);
                setExtraActivo(!!merged.recordatorio_extra_activo);
                setExtraFecha(toDateInputValue(merged.recordatorio_extra_fecha));
            }
            await fetchEncuestas();
            showToast('Recordatorio guardado');
        } catch (err) {
            const msg = err?.message || 'Error al guardar el recordatorio';
            setDetalleActionError(msg);
        } finally {
            setRecordatorioLoading(false);
        }
    };

    const handleForzarFinalizacion = async () => {
        if (!editingEncuesta?.id_encuesta) return;
        setDetalleActionError('');
        setDetalleActionLoading(true);
        try {
            const res = await execute(`${BASE_ENCUESTAS}/finalizarEncuestaAhora`, 'POST', { id_encuesta: editingEncuesta.id_encuesta }, { silent: true });
            if (res?.data?.encuesta) {
                syncEditingEncuesta(res.data.encuesta);
            }
            setFinalizarConfirm(false);
            await fetchEncuestas();
            showToast('Encuesta finalizada');
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Error al finalizar la encuesta';
            setDetalleActionError(msg);
        } finally {
            setDetalleActionLoading(false);
        }
    };

    const handleReabrirEncuesta = async () => {
        if (!editingEncuesta?.id_encuesta) return;
        const fin = reabrirFechaFin.trim();
        if (!fin) {
            setDetalleActionError('La fecha de cierre es obligatoria para reabrir la encuesta.');
            return;
        }
        setDetalleActionError('');
        setDetalleActionLoading(true);
        try {
            const body = { id_encuesta: editingEncuesta.id_encuesta, fecha_fin: fin };
            if (muestraOpcionCorreoReabrir) {
                body.enviar_correo_plazo = reabrirEnviarCorreoPlazo;
            }
            const res = await execute(`${BASE_ENCUESTAS}/reabrirEncuesta`, 'POST', body, { silent: true });
            if (res?.data?.encuesta) {
                const merged = { ...editingEncuesta, ...res.data.encuesta };
                syncEditingEncuesta(merged);
                setFechaInicio(toDateInputValue(merged.fecha_inicio));
                const nuevaFin = toDateInputValue(merged.fecha_fin);
                setFechaFin(nuevaFin);
                setFechaFinOriginal(nuevaFin);
                initGestionState(merged);
            }
            await fetchEncuestas();
            const enviados = res?.data?.correos_ampliacion_enviados;
            if (typeof enviados === 'number' && enviados > 0) {
                showToast(`Encuesta reabierta. Correo de ampliación enviado a ${enviados} participante${enviados === 1 ? '' : 's'}.`);
            } else {
                showToast('Encuesta reabierta');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Error al reabrir la encuesta';
            setDetalleActionError(msg);
        } finally {
            setDetalleActionLoading(false);
        }
    };

    const closeEnviarConfirm = (force = false) => {
        if (enviarLoading && !force) return;
        setEnviarConfirm(null);
        setPreflightResult(null);
        setPreflightLoading(false);
        setEnviarLoading(false);
    };

    const openEnviarPreflight = async (encuesta) => {
        setEnviarConfirm(encuesta);
        setPreflightLoading(true);
        setPreflightResult(null);
        try {
            const res = await execute(
                `${BASE_ENCUESTAS}/validarEnvioEncuesta?id_encuesta=${encuesta.id_encuesta}`,
                'GET',
                null,
                { silent: true }
            );
            setPreflightResult(res?.data || null);
        } catch (err) {
            setPreflightResult({ error: err?.message || 'No se pudo validar los correos' });
        } finally {
            setPreflightLoading(false);
        }
    };

    const labelPreflightEstado = (estado) => (
        { listo: 'Listo', advertencia: 'Advertencia', bloqueado: 'Bloqueado' }[estado] || estado
    );

    const badgePreflightClass = (estado) => {
        if (estado === 'listo') return styles.preflightBadgeListo;
        if (estado === 'advertencia') return styles.preflightBadgeAdvertencia;
        return styles.preflightBadgeBloqueado;
    };

    const detallePreflightRelevante = useMemo(() => {
        if (!preflightResult?.detalle) return [];
        return preflightResult.detalle.filter((d) => d.estado !== 'listo');
    }, [preflightResult]);

    const handleEnviarEncuestaAhora = async (encuesta) => {
        if (enviarLoading) return;
        setEnviarLoading(true);
        setPreflightResult((prev) => (prev ? { ...prev, sendError: null } : prev));
        try {
            const { data: res } = await execute(`${BASE_ENCUESTAS}/enviarEncuestaAhora`, 'POST', {
                id_encuesta: encuesta.id_encuesta,
            }, { silent: true });

            const enviados = res?.correos_enviados ?? 0;
            const omitidos = res?.correos_omitidos ?? 0;
            const errores = res?.correos_errores ?? 0;

            closeEnviarConfirm(true);

            if (editingEncuesta?.id_encuesta === encuesta.id_encuesta && res?.encuesta) {
                const merged = { ...editingEncuesta, ...res.encuesta };
                syncEditingEncuesta(merged);
                initGestionState(merged);
            }

            fetchEncuestas();

            if (omitidos > 0 || errores > 0) {
                showToast(
                    `Encuesta activada: ${enviados} enviados, ${omitidos} omitidos, ${errores} errores`
                );
            } else {
                showToast(`Encuesta enviada y activada (${enviados} correo${enviados === 1 ? '' : 's'})`);
            }
        } catch (err) {
            setPreflightResult((prev) => ({
                ...(prev || {}),
                sendError: err?.message || 'No se pudo enviar la encuesta',
            }));
            setEnviarLoading(false);
        }
    };

    const columns = [
        { header: 'Tipo', accessor: 'tipo', render: (row) => <strong>{labelTipo(row.tipo)}</strong> },
        {
            header: 'Empresa',
            accessor: 'nombre_empresa',
            render: (row) => (
                <span>{row.tipo === 'equipo' ? (row.nombre_empresa || '—') : '—'}</span>
            ),
        },
        {
            header: 'Equipo',
            accessor: 'asignacion',
            render: (row) => (
                <span className={styles.asignacionNombre}>{labelAsignacion(row)}</span>
            ),
        },
        { header: 'Fecha inicio', accessor: 'fecha_inicio', render: (row) => <span className={styles.fechaCell}>{formatFecha(row.fecha_inicio)}</span> },
        { header: 'Fecha fin', accessor: 'fecha_fin', render: (row) => <span className={styles.fechaCell}>{formatFecha(row.fecha_fin)}</span> },
        {
            header: 'Estado',
            accessor: 'estado',
            render: (row) => {
                const estado = row.estado || '';
                return (
                    <span className={`${styles.badge} ${badgeEstadoClass(estado, styles)}`}>
                        {labelEstado(estado)}
                    </span>
                );
            },
        },
        {
            header: '',
            accessor: 'acciones',
            render: (row) => (
                <div
                    className={styles.rowActionsCell}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <div className={styles.encuestaActionIcons}>
                        {row.estado === 'programada' && (
                            <button
                                type="button"
                                className={styles.encuestaSendBtn}
                                onClick={() => openEnviarPreflight(row)}
                                aria-label={`Enviar encuesta a ${labelAsignacion(row)}`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" aria-hidden="true">
                                    <path d="M120-160v-640l760 320-760 320Zm80-120 474-200-474-200v140l240 60-240 60v140Zm0 0v-400 400Z" fill="currentColor" />
                                </svg>
                            </button>
                        )}
                        <button
                            type="button"
                            className={styles.encuestaEditBtn}
                            onClick={() => openEdit(row)}
                            aria-label={`Editar encuesta de ${labelAsignacion(row)}`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" aria-hidden="true">
                                <path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z" fill="currentColor" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className={styles.encuestaDeleteBtn}
                            onClick={() => setDeleteConfirm(row)}
                            aria-label={`Eliminar encuesta de ${labelAsignacion(row)}`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" aria-hidden="true">
                                <path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z" fill="currentColor" />
                            </svg>
                        </button>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h2 className={styles.title}>Gestión de Encuestas</h2>
                    <p className={styles.subtitle}>Crea encuestas para un equipo o para una persona. Haz clic en una fila para ver resultados; usa el lápiz para gestionar plazos, recordatorios y acciones.</p>
                </div>
                <button type="button" className={styles.createBtn} onClick={openCreate}>
                    <span className={styles.createBtnIcon} aria-hidden>+</span>
                    Nueva encuesta
                </button>
            </div>

            {listError?.message && <p className={styles.errorMsg} role="alert">{listError.message}</p>}
            {loadingEncuestas ? (
                <p className={styles.loadingMsg}>Cargando encuestas…</p>
            ) : (
                <DataTable
                    title="Encuestas"
                    columns={columns}
                    data={encuestasFiltradas}
                    onRowClick={goToResultados}
                    getRowKey={(row) => row.id_encuesta}
                    compact
                    headerExtra={encuestas.length > 0 ? (
                        <div className={styles.toolbar}>
                            <div className={styles.filterTabs} role="tablist" aria-label="Filtrar por tipo">
                                {filtrosTipo.map((f) => (
                                    <button
                                        key={f.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={filtroTipo === f.id}
                                        className={`${styles.filterTab} ${filtroTipo === f.id ? styles.filterTabActive : ''}`}
                                        onClick={() => setFiltroTipo(f.id)}
                                    >
                                        {f.label}
                                        <span className={styles.filterTabCount}>{conteoPorTipo[f.id]}</span>
                                    </button>
                                ))}
                            </div>
                            {empresasDisponibles.length > 0 && (
                                <select
                                    className={styles.filterSelect}
                                    value={filtroEmpresa}
                                    onChange={(e) => setFiltroEmpresa(e.target.value)}
                                    disabled={filtroTipo === 'individual' || filtroTipo === 'freemium'}
                                    aria-label="Filtrar por empresa"
                                    title={
                                        filtroTipo === 'individual' || filtroTipo === 'freemium'
                                            ? 'Las encuestas individuales y freemium no pertenecen a una empresa'
                                            : undefined
                                    }
                                >
                                    <option value="todas">Todas las empresas</option>
                                    {empresasDisponibles.map((nombre) => (
                                        <option key={nombre} value={nombre}>
                                            {nombre} ({conteoPorEmpresa[nombre] || 0})
                                        </option>
                                    ))}
                                </select>
                            )}
                            {(filtroTipo !== 'todos' || filtroEmpresa !== 'todas') && (
                                <span className={styles.filterHint}>
                                    Mostrando {encuestasFiltradas.length} de {encuestas.length}
                                </span>
                            )}
                        </div>
                    ) : null}
                />
            )}

            {showFormModal && (
                <div className={styles.modalOverlay} onClick={closeFormModal}>
                    <div
                        className={`${styles.modalContent} ${editingEncuesta ? styles.modalContentWide : ''}`}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            <div className={styles.modalHeaderText}>
                                <h3>{editingEncuesta ? 'Gestionar encuesta' : 'Nueva encuesta'}</h3>
                                {editingEncuesta && (
                                    <p className={styles.modalSubtitle}>
                                        <span className={`${styles.badge} ${badgeEstadoClass(editingEncuesta.estado, styles)}`}>
                                            {labelEstado(editingEncuesta.estado)}
                                        </span>
                                        <span className={styles.modalSubtitleSep}>·</span>
                                        <span>{labelTipo(editingEncuesta.tipo)}</span>
                                        <span className={styles.modalSubtitleSep}>·</span>
                                        <span>{labelAsignacion(editingEncuesta)}</span>
                                        {editingEncuesta.tipo === 'equipo' && editingEncuesta.nombre_empresa && (
                                            <>
                                                <span className={styles.modalSubtitleSep}>·</span>
                                                <span>{editingEncuesta.nombre_empresa}</span>
                                            </>
                                        )}
                                    </p>
                                )}
                            </div>
                            <button type="button" className={styles.closeBtn} onClick={closeFormModal} aria-label="Cerrar">✕</button>
                        </div>

                        {!editingEncuesta ? (
                        <form onSubmit={handleSaveEncuesta} className={styles.form}>
                            {formError && <p className={styles.errorMsg} role="alert">{formError}</p>}

                            <div className={styles.inputGroup}>
                                <label>Tipo de encuesta</label>
                                <div className={styles.tipoSelector}>
                                    <label className={styles.tipoOption} onMouseDown={(e) => e.preventDefault()} onClick={clearChoiceSelection}>
                                        <input type="radio" name="tipo-encuesta" value="equipo" checked={tipoEncuesta === 'equipo'} onChange={() => handleTipoEncuestaChange('equipo')} onClick={clearChoiceSelection} />
                                        Equipo
                                    </label>
                                    <label className={styles.tipoOption} onMouseDown={(e) => e.preventDefault()} onClick={clearChoiceSelection}>
                                        <input type="radio" name="tipo-encuesta" value="individual" checked={tipoEncuesta === 'individual'} onChange={() => handleTipoEncuestaChange('individual')} onClick={clearChoiceSelection} />
                                        Individual
                                    </label>
                                </div>
                            </div>

                            {tipoEncuesta === 'equipo' && (
                                <div className={styles.inputGroup}>
                                    <label htmlFor="enc-equipo">Equipo</label>
                                    <select id="enc-equipo" value={idEquipo} onChange={e => setIdEquipo(e.target.value)} required className={styles.selectInput} disabled={loadingEquipos}>
                                        <option value="">{loadingEquipos ? 'Cargando equipos…' : 'Seleccione un equipo'}</option>
                                        {equiposList.map((eq) => (
                                            <option key={eq.id_equipo} value={eq.id_equipo}>{eq.nombre_empresa ? `${eq.nombre_empresa} — ${eq.nombre_equipo}` : eq.nombre_equipo}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {tipoEncuesta === 'individual' && (
                                <div className={styles.individualPanel}>
                                    <div>
                                        <h4 className={styles.individualPanelTitle}>Persona destinataria</h4>
                                        <p className={styles.individualPanelHint}>
                                            Introduce los datos de quien recibirá la encuesta. Si el correo ya existe, se reutilizará ese usuario.
                                        </p>
                                    </div>
                                    <div className={styles.individualFieldsRow}>
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="enc-nombre">Nombre</label>
                                            <input id="enc-nombre" type="text" value={personaNombre} onChange={e => setPersonaNombre(e.target.value)} placeholder="Nombre" required autoComplete="given-name" />
                                        </div>
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="enc-apellido">Apellido</label>
                                            <input id="enc-apellido" type="text" value={personaApellido} onChange={e => setPersonaApellido(e.target.value)} placeholder="Apellido" required autoComplete="family-name" />
                                        </div>
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label htmlFor="enc-email">Correo electrónico</label>
                                        <input id="enc-email" type="email" value={personaEmail} onChange={e => setPersonaEmail(e.target.value)} placeholder="correo@ejemplo.com" required autoComplete="email" />
                                    </div>
                                    <div className={styles.individualDivider} />
                                    <div className={styles.inputGroup}>
                                        <label>Envío</label>
                                        <div className={styles.entregaSelector}>
                                            <label className={styles.entregaOption} onMouseDown={(e) => e.preventDefault()} onClick={clearChoiceSelection}>
                                                <input type="radio" name="modo-entrega" value="correo" checked={modoEntrega === 'correo'} onChange={() => handleModoEntregaChange('correo')} onClick={clearChoiceSelection} />
                                                Por correo
                                            </label>
                                            <label className={styles.entregaOption} onMouseDown={(e) => e.preventDefault()} onClick={clearChoiceSelection}>
                                                <input type="radio" name="modo-entrega" value="enlace" checked={modoEntrega === 'enlace'} onChange={() => handleModoEntregaChange('enlace')} onClick={clearChoiceSelection} />
                                                Solo enlace
                                            </label>
                                            <label className={styles.entregaOption} onMouseDown={(e) => e.preventDefault()} onClick={clearChoiceSelection}>
                                                <input type="radio" name="modo-entrega" value="programar" checked={modoEntrega === 'programar'} onChange={() => handleModoEntregaChange('programar')} onClick={clearChoiceSelection} />
                                                Programar
                                            </label>
                                        </div>
                                        <span className={styles.inputHint}>
                                            {modoEntrega === 'correo' && 'Activa la encuesta y envía el enlace al correo indicado.'}
                                            {modoEntrega === 'enlace' && 'Activa la encuesta sin correo; podrás copiar el enlace desde la gestión (icono lápiz).'}
                                            {modoEntrega === 'programar' && 'La encuesta queda pendiente hasta que la envíes o actives manualmente.'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className={styles.inputRow}>
                                <div className={styles.inputGroup}>
                                    <label htmlFor="enc-inicio">Fecha de inicio (obligatoria)</label>
                                    <input id="enc-inicio" type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} required />
                                    <span className={styles.inputHint}>Primer día en que la encuesta está disponible.</span>
                                </div>
                                <div className={styles.inputGroup}>
                                    <label htmlFor="enc-fin">Fecha de cierre (obligatoria)</label>
                                    <input id="enc-fin" type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} min={fechaInicio || undefined} required />
                                    <span className={styles.inputHint}>Último día para responder.</span>
                                </div>
                            </div>

                            <div className={styles.formActions}>
                                <button type="button" className={styles.actionBtn} onClick={closeFormModal}>Cancelar</button>
                                <button type="submit" className={styles.submitBtn}>Crear encuesta</button>
                            </div>
                        </form>
                        ) : (
                        <div className={styles.gestionModalBody}>
                            {formError && <p className={styles.errorMsg} role="alert">{formError}</p>}
                            {detalleActionError && <p className={styles.errorMsg} role="alert">{detalleActionError}</p>}

                            {(editingEncuesta.tipo === 'individual' || editingEncuesta.tipo === 'freemium') && editingEncuesta.email_usuario && (
                                <p className={styles.gestionMetaEmail}>{editingEncuesta.email_usuario}</p>
                            )}

                            <section className={styles.plazosHero}>
                                <div className={styles.plazosHeroHead}>
                                    <h4 className={styles.plazosHeroTitle}>Plazos de la encuesta</h4>
                                    <p className={styles.plazosHeroHint}>Lo más habitual es ajustar aquí las fechas de inicio y cierre.</p>
                                </div>
                                <form onSubmit={handleSaveEncuesta} className={styles.plazosHeroForm}>
                                    <div className={styles.plazosHeroDates}>
                                        <div className={styles.plazosDateField}>
                                            <label htmlFor="enc-inicio-edit">Inicio</label>
                                            <input id="enc-inicio-edit" type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
                                            <span className={styles.plazosDatePreview}>{formatFecha(fechaInicio) || 'Sin definir'}</span>
                                        </div>
                                        <div className={styles.plazosDateArrow} aria-hidden>→</div>
                                        <div className={styles.plazosDateField}>
                                            <label htmlFor="enc-fin-edit">Cierre (obligatorio)</label>
                                            <input id="enc-fin-edit" type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} min={fechaInicio || undefined} required />
                                            <span className={styles.plazosDatePreview}>{formatFecha(fechaFin)}</span>
                                        </div>
                                    </div>
                                    {muestraOpcionCorreoPlazo && (
                                        <label className={styles.checkboxRow}>
                                            <input type="checkbox" checked={enviarCorreoPlazo} onChange={(e) => setEnviarCorreoPlazo(e.target.checked)} />
                                            Enviar correo de ampliación de plazo a los participantes
                                        </label>
                                    )}
                                    <button type="submit" className={styles.submitBtn}>Guardar plazos</button>
                                </form>
                            </section>

                            <section className={styles.gestionSection}>
                                <h4 className={styles.gestionSectionTitle}>Recordatorios por correo</h4>
                                <div className={styles.recordatoriosGrid}>
                                    <div className={styles.recordatorioFijo}>
                                        <div className={styles.recordatorioFijoHeader}>
                                            <span className={styles.recordatorioBadgeAuto}>Automático</span>
                                            <strong>24 h antes del cierre</strong>
                                        </div>
                                        <p className={styles.recordatorioDesc}>
                                            A las <strong>09:00 (España)</strong> a quienes no hayan completado las 40 respuestas.
                                        </p>
                                        <div className={styles.recordatorioFechaBlock}>
                                            <span className={styles.recordatorioFechaLabel}>Envío</span>
                                            <span className={styles.recordatorioFechaGrande}>
                                                {diaEnvioRecordatorio24h(fechaFin || editingEncuesta.fecha_fin)}
                                            </span>
                                            <span className={styles.recordatorioFechaHora}>09:00</span>
                                        </div>
                                        {editingEncuesta.recordatorio_24h_enviado ? (
                                            <p className={styles.recordatorioOk}>Ya enviado</p>
                                        ) : editingEncuesta.estado === 'activa' ? (
                                            <p className={styles.recordatorioPending}>Pendiente</p>
                                        ) : (
                                            <p className={styles.recordatorioMuted}>Cuando esté activa</p>
                                        )}
                                    </div>

                                    <div className={styles.recordatorioExtra}>
                                        <div className={styles.recordatorioFijoHeader}>
                                            <span className={styles.recordatorioBadgeExtra}>Opcional</span>
                                            <strong>Recordatorio adicional</strong>
                                        </div>
                                        <p className={styles.recordatorioDesc}>
                                            Elige un día; se envía a las <strong>09:00 (España)</strong>.
                                        </p>
                                        {editingEncuesta.estado !== 'activa' ? (
                                            <p className={styles.recordatorioMuted}>Disponible con la encuesta activa.</p>
                                        ) : (
                                            <>
                                                <label className={styles.checkboxRow}>
                                                    <input type="checkbox" checked={extraActivo} onChange={(e) => setExtraActivo(e.target.checked)} />
                                                    <span>Activar</span>
                                                </label>
                                                {extraActivo && (
                                                    <div className={styles.recordatorioForm}>
                                                        <div className={styles.inputGroup}>
                                                            <label htmlFor="rec-extra-fecha">Día de envío</label>
                                                            <input id="rec-extra-fecha" type="date" value={extraFecha} onChange={(e) => setExtraFecha(e.target.value)} min={toDateInputValue(new Date())} />
                                                        </div>
                                                        {extraFecha && !editingEncuesta.recordatorio_extra_enviado && (
                                                            <div className={styles.recordatorioFechaBlock}>
                                                                <span className={styles.recordatorioFechaLabel}>Se enviará</span>
                                                                <span className={styles.recordatorioFechaGrande}>{formatFecha(extraFecha)}</span>
                                                                <span className={styles.recordatorioFechaHora}>09:00</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {editingEncuesta.recordatorio_extra_enviado && (
                                                    <p className={styles.recordatorioOk}>Ya enviado</p>
                                                )}
                                                <button type="button" className={styles.submitBtnOutline} onClick={handleGuardarRecordatorioExtra} disabled={recordatorioLoading || detalleActionLoading}>
                                                    {recordatorioLoading ? 'Guardando…' : 'Guardar recordatorio'}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </section>

                            {(editingEncuesta.tipo === 'individual' || editingEncuesta.tipo === 'freemium') && (
                                <section className={styles.gestionSection}>
                                    <h4 className={styles.gestionSectionTitle}>Enlace de acceso</h4>
                                    <div className={styles.enlaceSection}>
                                        <p className={styles.enlaceDesc}>
                                          {editingEncuesta.tipo === 'freemium'
                                            ? 'Enlace freemium generado desde la web pública.'
                                            : 'Enlace personal, aunque la encuesta se haya enviado por correo.'}
                                        </p>
                                        <div className={styles.enlaceRow}>
                                            <input className={styles.enlaceInput} type="text" readOnly value={loadingEnlace ? 'Cargando…' : (enlaceDetalle || '')} placeholder={loadingEnlace ? '' : 'Obtener enlace'} />
                                            <button type="button" className={styles.copyBtn} onClick={() => handleCopiarEnlace(enlaceDetalle)} disabled={!enlaceDetalle || loadingEnlace}>Copiar</button>
                                        </div>
                                        <button type="button" className={styles.submitBtnOutline} onClick={() => cargarEnlaceEncuesta(editingEncuesta)} disabled={loadingEnlace}>
                                            {loadingEnlace ? 'Obteniendo…' : 'Obtener enlace'}
                                        </button>
                                        {enlaceCopiado && <p className={styles.enlaceOk}>Enlace copiado</p>}
                                    </div>
                                </section>
                            )}

                            <section className={styles.gestionSection}>
                                <h4 className={styles.gestionSectionTitle}>Estado de la encuesta</h4>
                                <div className={styles.accionesPanel}>
                                    {editingEncuesta.estado === 'programada' && (
                                        <div className={styles.accionFila}>
                                            <div className={styles.accionFilaTexto}>
                                                <strong>Enviar ahora</strong>
                                                <span>Activa la encuesta y envía los correos sin esperar al envío automático.</span>
                                            </div>
                                            <button type="button" className={styles.submitBtnOutline} onClick={() => openEnviarPreflight(editingEncuesta)} disabled={detalleActionLoading || enviarLoading}>
                                                Enviar
                                            </button>
                                        </div>
                                    )}

                                    {editingEncuesta.estado === 'activa' && (
                                        <div className={styles.accionFila}>
                                            <div className={styles.accionFilaTexto}>
                                                <strong>Finalizar antes de tiempo</strong>
                                                <span>Cierra la encuesta; los participantes no podrán seguir respondiendo.</span>
                                            </div>
                                            {!finalizarConfirm ? (
                                                <button type="button" className={styles.btnOutlineDanger} onClick={() => setFinalizarConfirm(true)} disabled={detalleActionLoading}>
                                                    Finalizar
                                                </button>
                                            ) : (
                                                <div className={styles.inlineConfirm}>
                                                    <button type="button" className={styles.actionBtn} onClick={() => setFinalizarConfirm(false)} disabled={detalleActionLoading}>No</button>
                                                    <button type="button" className={styles.btnOutlineDanger} onClick={handleForzarFinalizacion} disabled={detalleActionLoading}>
                                                        {detalleActionLoading ? '…' : 'Confirmar'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {editingEncuesta.estado === 'finalizada' && (
                                        <div className={styles.accionFilaStacked}>
                                            <div className={styles.accionFilaTexto}>
                                                <strong>Reabrir encuesta</strong>
                                                <span>Vuelve a permitir respuestas con una nueva fecha de cierre obligatoria.</span>
                                            </div>
                                            <div className={styles.reabrirInline}>
                                                <input
                                                    id="reabrir-fecha-fin"
                                                    type="date"
                                                    className={styles.reabrirDateInput}
                                                    value={reabrirFechaFin}
                                                    onChange={(e) => setReabrirFechaFin(e.target.value)}
                                                    min={toDateInputValue(editingEncuesta.fecha_inicio) || undefined}
                                                    required
                                                    aria-label="Nueva fecha de cierre"
                                                />
                                                {muestraOpcionCorreoReabrir && (
                                                    <label className={styles.checkboxRowCompact}>
                                                        <input type="checkbox" checked={reabrirEnviarCorreoPlazo} onChange={(e) => setReabrirEnviarCorreoPlazo(e.target.checked)} />
                                                        Avisar por correo
                                                    </label>
                                                )}
                                                <button type="button" className={styles.submitBtnOutline} onClick={handleReabrirEncuesta} disabled={detalleActionLoading}>
                                                    {detalleActionLoading ? 'Reabriendo…' : 'Reabrir'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </section>

                            <div className={styles.gestionFooter}>
                                <button type="button" className={styles.actionBtn} onClick={closeFormModal}>Cerrar</button>
                                <button type="button" className={styles.submitBtnSecondary} onClick={() => goToResultados(editingEncuesta)}>
                                    Ver resultados
                                </button>
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            )}

            {enviarConfirm && (
                <div className={styles.modalOverlay} onClick={() => closeEnviarConfirm()}>
                    <div className={`${styles.modalContent} ${styles.modalContentWide}`} onClick={e => e.stopPropagation()}>
                        {enviarLoading && (
                            <div className={styles.modalSendingOverlay} role="status" aria-live="polite">
                                <div className={styles.spinner} aria-hidden="true" />
                                <p className={styles.modalSendingText}>Enviando correos…</p>
                                <p className={styles.modalSendingHint}>Esto puede tardar unos segundos según el número de participantes.</p>
                            </div>
                        )}

                        <div className={enviarLoading ? styles.modalContentDimmed : undefined}>
                        <div className={styles.modalHeader}>
                            <h3>Enviar encuesta ahora</h3>
                            <button
                                type="button"
                                className={styles.closeBtn}
                                onClick={() => closeEnviarConfirm()}
                                disabled={enviarLoading}
                                aria-label="Cerrar"
                            >
                                ✕
                            </button>
                        </div>
                        <p className={styles.confirmText}>
                            {enviarConfirm.tipo === 'individual'
                                ? 'Se enviará de inmediato el correo con el enlace a la persona asignada. La encuesta pasará a estado activa.'
                                : 'Se enviarán los correos con el enlace a los miembros del equipo. La encuesta pasará a estado activa.'}
                        </p>

                        {preflightLoading && (
                            <div className={styles.preflightLoading} role="status">
                                <div className={styles.spinner} aria-hidden="true" />
                                <span>Comprobando correos de los participantes…</span>
                            </div>
                        )}

                        {!preflightLoading && preflightResult?.error && (
                            <p className={styles.errorMsg} role="alert">{preflightResult.error}</p>
                        )}

                        {!preflightLoading && preflightResult?.sendError && (
                            <p className={styles.errorMsg} role="alert">{preflightResult.sendError}</p>
                        )}

                        {!preflightLoading && preflightResult && !preflightResult.error && (
                            <>
                                <div className={styles.preflightSummary}>
                                    <div className={`${styles.preflightStat} ${styles.preflightStatOk}`}>
                                        <strong>{preflightResult.listos ?? 0}</strong>
                                        <span>Listos</span>
                                    </div>
                                    <div className={`${styles.preflightStat} ${styles.preflightStatWarn}`}>
                                        <strong>{preflightResult.advertencias ?? 0}</strong>
                                        <span>Advertencias</span>
                                    </div>
                                    <div className={`${styles.preflightStat} ${styles.preflightStatBlock}`}>
                                        <strong>{preflightResult.bloqueados ?? 0}</strong>
                                        <span>Bloqueados</span>
                                    </div>
                                </div>

                                {preflightResult.mensaje && (
                                    <p className={styles.errorMsg} role="alert">{preflightResult.mensaje}</p>
                                )}

                                {preflightResult.top_dominios?.length > 0 && (
                                    <p className={styles.preflightDomains}>
                                        {preflightResult.dominios_unicos} dominio{preflightResult.dominios_unicos === 1 ? '' : 's'} distintos.
                                        {' '}Principal: {preflightResult.top_dominios[0].dominio} ({preflightResult.top_dominios[0].count}).
                                    </p>
                                )}

                                {detallePreflightRelevante.length > 0 && (
                                    <div className={styles.preflightList}>
                                        {detallePreflightRelevante.map((item) => (
                                            <div key={`${item.id_usuario}-${item.email}`} className={styles.preflightItem}>
                                                <div className={styles.preflightItemHeader}>
                                                    <span className={styles.preflightEmail}>{item.email}</span>
                                                    {item.nombre && (
                                                        <span className={styles.preflightNombre}>{item.nombre}</span>
                                                    )}
                                                    <span className={`${styles.preflightBadge} ${badgePreflightClass(item.estado)}`}>
                                                        {labelPreflightEstado(item.estado)}
                                                    </span>
                                                </div>
                                                {item.motivos?.length > 0 && (
                                                    <ul className={styles.preflightMotivos}>
                                                        {item.motivos.map((m) => (
                                                            <li key={m}>{m}</li>
                                                        ))}
                                                        {item.sugerencia && (
                                                            <li>Sugerencia: {item.sugerencia}</li>
                                                        )}
                                                    </ul>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {preflightResult.bloqueados > 0 && preflightResult.listos > 0 && (
                                    <p className={styles.confirmText}>
                                        Los correos bloqueados se omitirán automáticamente; el resto se enviará con normalidad.
                                    </p>
                                )}
                            </>
                        )}

                        <div className={styles.formActions}>
                            <button type="button" className={styles.actionBtn} onClick={() => closeEnviarConfirm()} disabled={enviarLoading}>Cancelar</button>
                            <button
                                type="button"
                                className={styles.submitBtn}
                                onClick={() => handleEnviarEncuestaAhora(enviarConfirm)}
                                disabled={
                                    enviarLoading
                                    || preflightLoading
                                    || (preflightResult && preflightResult.puede_enviar === false)
                                }
                            >
                                {enviarLoading ? 'Enviando…' : 'Enviar ahora'}
                            </button>
                        </div>
                        </div>
                    </div>
                </div>
            )}

            {deleteConfirm && (
                <div className={`${styles.modalOverlay} ${styles.modalOverlayNested}`} onClick={() => setDeleteConfirm(null)}>
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Eliminar encuesta</h3>
                            <button type="button" className={styles.closeBtn} onClick={() => setDeleteConfirm(null)} aria-label="Cerrar">✕</button>
                        </div>
                        <p className={styles.confirmText}>
                            ¿Eliminar la encuesta de &quot;{labelAsignacion(deleteConfirm)}&quot; (del {formatFecha(deleteConfirm.fecha_inicio)} al {formatFecha(deleteConfirm.fecha_fin)})? Esta acción no se puede deshacer.
                        </p>
                        <div className={styles.formActions}>
                            <button type="button" className={styles.actionBtn} onClick={() => setDeleteConfirm(null)}>Cancelar</button>
                            <button type="button" className={styles.dangerBtn} onClick={() => handleDeleteEncuesta(deleteConfirm)}>Eliminar</button>
                        </div>
                    </div>
                </div>
            )}


            {toastMsg && <div className={styles.toast} role="status">{toastMsg}</div>}

        </div>
    );
};

export default Encuestas;
